import axios from 'axios';

export interface ChatThread {
  thread_id: string;
  appointment_id: string;
  property_name: string;
  client_name: string;
  client_id?: string;
  employee_name: string;
  employee_id: string;
  last_message?: string;
  last_sender_type?: string;
  last_message_at?: string;
  message_count: number;
  unread_count: number;
}

export interface HelpRequest {
  id: string;
  priority?: string;
  status?: string;
  description?: string;
}

export interface ChatMessage {
  id: string;
  appointment_id: string;
  sender_id: string;
  sender_type: string;
  sender_name: string;
  message: string;
  message_type: string;
  attachment_url?: string | null;
  is_read: boolean;
  read_at?: string | null;
  created_at: string;
  help_request?: HelpRequest | null;
}

interface ListThreadsResponse {
  threads: ChatThread[];
  count: number;
}

interface GetMessagesResponse {
  messages: ChatMessage[];
}

interface SendMessageRequest {
  message: string;
  message_type?: string;
  priority?: string;
  description?: string;
  send_via_whatsapp?: boolean; // Nova opção para enviar também via WhatsApp
}

export interface WebsocketMessageRequest {
  type?: string;
  message: string;
  priority?: string;
  description?: string;
}

const chatAPI = axios.create({
  baseURL: '/api/v1/chat',
  headers: {
    'Content-Type': 'application/json',
  },
});

chatAPI.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function buildWebSocketUrl(appointmentId: string): string {
  const token = localStorage.getItem('token');
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  const query = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${protocol}//${host}/api/v1/chat/appointments/${appointmentId}/ws${query}`;
}

export interface ChatSocketConnection {
  socket: WebSocket;
  sendMessage: (payload: WebsocketMessageRequest) => void;
  close: () => void;
}

export function connectToAppointment(
  appointmentId: string,
  onMessage: (payload: any) => void,
  onError?: (event: Event) => void,
  onClose?: (event: CloseEvent) => void
): ChatSocketConnection {
  const url = buildWebSocketUrl(appointmentId);
  const socket = new WebSocket(url);

  socket.onmessage = event => {
    try {
      const data = JSON.parse(event.data);
      onMessage?.(data);
    } catch (error) {
      console.warn('[chatService] Failed to parse websocket data', error);
    }
  };

  if (onError) {
    socket.onerror = onError;
  }

  if (onClose) {
    socket.onclose = onClose;
  }

  const sendMessage = (payload: WebsocketMessageRequest) => {
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(payload));
    }
  };

  const close = () => {
    try {
      socket.close();
    } catch (error) {
      console.warn('[chatService] Failed to close websocket', error);
    }
  };

  return { socket, sendMessage, close };
}

async function listThreads(params?: {
  appointment_id?: string;
  client_id?: string;
  limit?: number;
  offset?: number;
}) {
  try {
    const response = await chatAPI.get<ListThreadsResponse>('/threads', { params });
    return {
      threads: Array.isArray(response.data?.threads) ? response.data.threads : [],
      count: response.data?.count || 0,
    };
  } catch (error) {
    console.error('[chatService] Failed to list threads', error);
    return { threads: [], count: 0 };
  }
}

async function getMessages(appointmentId: string, limit = 50, offset = 0) {
  try {
    const response = await chatAPI.get<GetMessagesResponse>(
      `/appointments/${appointmentId}/messages`,
      {
        params: { limit, offset },
      }
    );
    return Array.isArray(response.data?.messages) ? response.data.messages : [];
  } catch (error) {
    console.error('[chatService] Failed to get messages', error);
    return [];
  }
}

async function sendMessage(appointmentId: string, payload: SendMessageRequest) {
  const response = await chatAPI.post<{ payload: ChatMessage }>(
    `/appointments/${appointmentId}/messages`,
    payload
  );
  return response.data.payload;
}

async function markAllRead(appointmentId: string) {
  await chatAPI.post(`/appointments/${appointmentId}/read`);
}

export const chatService = {
  listThreads,
  getMessages,
  sendMessage,
  markAllRead,
  connectToAppointment,
};

export default chatService;
